import React, { useRef, useState } from 'react';
import { ChevronDown, Check, FolderGit2, Plus } from 'lucide-react';
import PopoverPortal from '../common/PopoverPortal';
import { useApplication } from '../../contexts/ApplicationContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useScanProgress } from '../../contexts/ScanProgressContext';
import { getApplicationDisplayTitle } from '../../utils/repository';
import CreateApplicationModal from './CreateApplicationModal';

interface RepositorySwitcherProps {
  /** Collapse to an icon-only trigger (expands on hover via the sidebar's group). */
  collapsed?: boolean;
}

/**
 * Repository switcher rendered at the top of the application sidebar. Lists the
 * organization's repositories and switches between them; also hosts the "Add repository"
 * action. Replaces the old standalone repositories list page as the primary way to switch.
 */
const RepositorySwitcher: React.FC<RepositorySwitcherProps> = ({ collapsed = false }) => {
  const {
    currentApplication,
    applicationId,
    applications,
    navigateToApplication,
    refreshApplications,
  } = useApplication();
  const { currentOrganization } = useOrganization();
  const { state: scanState } = useScanProgress();

  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const isReadOnly =
    !!currentOrganization?.is_demo ||
    currentOrganization?.current_user_role === 'viewer';

  const handleToggle = () => {
    setSearch('');
    setIsOpen((open) => !open);
  };

  const handleSelect = (id: string) => {
    setIsOpen(false);
    if (id !== applicationId) {
      navigateToApplication(id);
    }
  };

  const handleCreated = (application: { id: string }) => {
    refreshApplications();
    navigateToApplication(application.id);
  };

  // Prefer the fully-loaded application; fall back to the list entry so the name doesn't
  // flash "Select repository" while the detailed fetch is still in flight.
  const activeApp =
    currentApplication ?? applications.find((app) => app.id === applicationId) ?? null;
  const currentName = activeApp ? getApplicationDisplayTitle(activeApp) : 'Select repository';

  const filtered = search
    ? applications.filter((app) =>
        getApplicationDisplayTitle(app).toLowerCase().includes(search.toLowerCase()),
      )
    : applications;

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger */}
      <button
        onClick={handleToggle}
        className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-[10px] transition-colors hover:bg-[var(--surface-2)] ${
          collapsed ? 'justify-center group-hover:justify-start' : ''
        }`}
        style={{ border: '1px solid var(--border-subtle)' }}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Switch repository"
        title="Switch repository"
      >
        <FolderGit2 className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--accent-ink)' }} />
        <div className={`flex-1 min-w-0 text-left leading-tight ${collapsed ? 'hidden group-hover:block' : ''}`}>
          <div className="text-[9px] font-bold uppercase tracking-wider text-[color:var(--text-muted)]">
            Repository
          </div>
          <div className="text-[13.5px] font-semibold truncate text-[color:var(--text-primary)]" style={{ fontFamily: 'var(--font-mono)' }}>
            {currentName}
          </div>
        </div>
        {scanState === 'running' && (
          <span
            className="w-1.5 h-1.5 flex-shrink-0 rounded-full bg-brand-primary animate-pulse"
            title="Scan running"
            aria-label="Scan running"
          />
        )}
        <ChevronDown
          className={`w-4 h-4 flex-shrink-0 text-[color:var(--text-muted)] transition-transform ${isOpen ? 'rotate-180' : ''} ${
            collapsed ? 'hidden group-hover:block' : ''
          }`}
        />
      </button>

      {/* Dropdown */}
      <PopoverPortal
        anchorRef={containerRef}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        width={256}
        className="bg-ui-background-secondary border border-ui-border-primary rounded-lg shadow-lg overflow-hidden"
      >
          <div className="px-3 pt-2 pb-1 text-[10px] font-medium uppercase tracking-wider text-ui-text-tertiary">
            Switch repository
          </div>

          {applications.length > 5 && (
            <div className="px-2 pb-2">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter repositories..."
                autoFocus
                className="w-full px-2.5 py-1.5 text-xs border border-ui-border-primary rounded bg-ui-background-secondary text-ui-text-primary placeholder-ui-text-tertiary dark:placeholder-ui-text-tertiary focus:ring-1 focus:ring-brand-primary focus:border-transparent outline-none"
              />
            </div>
          )}

          <div className="max-h-60 overflow-y-auto" role="listbox">
            {filtered.length === 0 ? (
              <p className="text-xs text-ui-text-tertiary text-center py-4">
                {search ? 'No matching repositories' : 'No repositories'}
              </p>
            ) : (
              filtered.map((app) => {
                const isActive = app.id === applicationId;
                return (
                  <button
                    key={app.id}
                    onClick={() => handleSelect(app.id)}
                    role="option"
                    aria-selected={isActive}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 hover:bg-ui-background-tertiary transition-colors ${
                      isActive
                        ? 'bg-brand-primary/10 text-brand-primary dark:text-brand-primaryDarkMode font-medium'
                        : 'text-ui-text-secondary'
                    }`}
                  >
                    <span className="truncate">{getApplicationDisplayTitle(app)}</span>
                    {isActive && <Check className="w-3.5 h-3.5 shrink-0" />}
                  </button>
                );
              })
            )}
          </div>

          {/* Add repository */}
          <div className="border-t border-ui-border-primary">
            <button
              onClick={() => {
                setIsOpen(false);
                setIsCreateOpen(true);
              }}
              disabled={isReadOnly}
              title={isReadOnly ? 'You do not have permission to add repositories' : undefined}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-ui-text-secondary hover:bg-ui-background-tertiary transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            >
              <Plus className="w-4 h-4" />
              Add repository
            </button>
          </div>
      </PopoverPortal>

      {currentOrganization && (
        <CreateApplicationModal
          isOpen={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          organizationId={currentOrganization.id}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
};

export default RepositorySwitcher;
